import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from "@devhop/ui";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "@/modules/reports/presentation/ui/ReportSummaryStatCard";
import { CompactMoneyAmount } from "@/shared/ui/CompactMoneyAmount";
import {
  BanknoteIcon,
  CalendarIcon,
  CheckCircleIcon,
  DownloadIcon,
  ExternalLinkIcon,
  FileTextIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import {
  usePerformanceEventReportQuery,
  usePerformanceReportSummaryQuery,
} from "../api/queries";
import {
  PerformanceReportDateFilter,
  usePerformanceReportDates,
  type PerformanceReportSearch,
} from "../ui/PerformanceReportDateFilter";
import {
  PerformanceReportsNav,
  escapeCsvCell,
  formatCurrencyBuckets,
  formatCurrencyBucketsInline,
  formatPerformanceDateTime,
  formatPerformanceMoney,
  summaryStatCurrencyBucketOptions,
} from "../ui/PerformanceReportsNav";

const STATUS_LABELS = {
  draft: "ຮ່າງ",
  confirmed: "ຢືນຢັນແລ້ວ",
  completed: "ສຳເລັດ",
  cancelled: "ຍົກເລີກ",
} as const;

const STATUS_VARIANTS = {
  draft: "secondary",
  confirmed: "default",
  completed: "outline",
  cancelled: "destructive",
} as const;

export function PerformanceEventReportPage() {
  const nav = useNavigate({ from: "/app/performance/reports/events" });
  const search = useSearch({
    from: "/app/performance/reports/events",
  }) as PerformanceReportSearch;

  const view = search.view ?? "overview";

  const { dateFrom, dateTo } = usePerformanceReportDates(search);
  const offset = search.offset ?? 0;
  const limit = 20;

  const query = useMemo(
    () => ({
      dateFrom,
      dateTo,
      status: search.status as
        | "draft"
        | "confirmed"
        | "completed"
        | "cancelled"
        | undefined,
      offset,
      limit,
    }),
    [dateFrom, dateTo, search.status, offset],
  );

  const summaryQuery = useMemo(
    () => ({ dateFrom, dateTo, groupBy: "day" as const }),
    [dateFrom, dateTo],
  );

  const { data, isLoading } = usePerformanceEventReportQuery(query);
  const { data: rollup, isLoading: summaryLoading } =
    usePerformanceReportSummaryQuery(summaryQuery);
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  const exportCsv = useCallback(() => {
    const header =
      view === "profitability"
        ? "ຊື່ງານ,ວັນທີງານ,ໃບສະເໜີລາຄາ,ລາຍຮັບ,ຕ່າງກັນ,ເກັບ%,ຈ່າຍແລ້ວ,ກຳໄລ,ກຳໄລ%"
        : "ຊື່ງານ,ວັນທີງານ,ສະຖານທີ່,ສະຖານະ,ນັກສະແດງ,ໃບສະເໜີລາຄາ,ລາຍຮັບ,ຈ່າຍແລ້ວ,ກຳໄລ,ກຳໄລ%";
    const csvRows = rows.map((row) =>
      view === "profitability"
        ? [
          escapeCsvCell(row.title),
          escapeCsvCell(formatPerformanceDateTime(row.eventDate)),
          escapeCsvCell(formatCurrencyBucketsInline(row.quoted)),
          escapeCsvCell(formatCurrencyBucketsInline(row.revenue)),
          escapeCsvCell(formatCurrencyBucketsInline(row.quotedVsActual)),
          escapeCsvCell(row.quotedFulfillmentPercent ?? ""),
          escapeCsvCell(row.payoutPaid),
          escapeCsvCell(row.margin ?? ""),
          escapeCsvCell(row.marginPercent ?? ""),
        ].join(",")
        : [
          escapeCsvCell(row.title),
          escapeCsvCell(formatPerformanceDateTime(row.eventDate)),
          escapeCsvCell(row.locationText ?? ""),
          escapeCsvCell(row.status),
          escapeCsvCell(row.assignmentCount),
          escapeCsvCell(formatCurrencyBucketsInline(row.quoted)),
          escapeCsvCell(formatCurrencyBucketsInline(row.revenue)),
          escapeCsvCell(row.payoutPaid),
          escapeCsvCell(row.margin ?? ""),
          escapeCsvCell(row.marginPercent ?? ""),
        ].join(","),
    );
    const csv = [header, ...csvRows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `performance-events-report-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, dateFrom, dateTo, view]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ", to: "/app/performance/events" },
            { label: "ລາຍງານງານສະແດງ", to: "/app/performance/reports" },
            { label: "ງານສະແດງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານງານສະແດງ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ຕາຕະລາງງານ ແລະ ກຳໄລຂັ້ນຕົ້ນຕໍ່ງານ
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={rows.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <PerformanceReportsNav />

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <PerformanceReportDateFilter
            routeFrom="/app/performance/reports/events"
            showEventStatus
          />
        </div>

        <ReportSummaryStatGrid columns={4}>
          <ReportSummaryStatCard
            title="ງານໃນຊ່ວງວັນທີ"
            icon={CalendarIcon}
            tone="packages"
            isLoading={summaryLoading}
            value={reportSummaryCount(rollup?.events.total ?? 0)}
          />
          <ReportSummaryStatCard
            title="ງານສຳເລັດ"
            icon={CheckCircleIcon}
            tone="revenue"
            isLoading={summaryLoading}
            value={reportSummaryCount(rollup?.events.completed ?? 0)}
          />
          <ReportSummaryStatCard
            title="ໃບສະເໜີລາຄາ (ລວມ)"
            icon={FileTextIcon}
            tone="attendance"
            isLoading={summaryLoading}
            value={formatCurrencyBuckets(
              rollup?.quoted,
              summaryStatCurrencyBucketOptions,
            )}
          />
          <ReportSummaryStatCard
            title="ຈ່າຍນັກສະແດງແລ້ວ"
            icon={BanknoteIcon}
            tone="students"
            isLoading={summaryLoading}
            value={
              <CompactMoneyAmount
                amount={rollup?.payoutPaid ?? 0}
                className="text-xl sm:text-2xl"
              />
            }
          />
        </ReportSummaryStatGrid>

        <div className="mb-4 flex gap-1 border-b">
          {(
            [
              { id: "overview", label: "ພາບລວມງານ" },
              { id: "profitability", label: "ສະເໜີ vs ເກັບແລ້ວ" },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() =>
                nav({ search: { ...search, view: tab.id, offset: 0 } })
              }
              className={cn(
                "-mb-px border-b-2 px-4 py-2 text-sm transition-colors",
                view === tab.id
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {view === "profitability"
                ? "ສຽບທຽນໃບສະເໜີລາຄາ ກັບ ເງິນທີ່ເກັບແລ້ວ"
                : "ຕາຕະລາງງານ"}{" "}
              ({total})
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isLoading ? (
              <p className="text-muted-foreground text-sm">ກຳລັງໂຫລດ...</p>
            ) : rows.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ບໍ່ມີຂໍ້ມູນໃນຊ່ວງວັນທີທີ່ເລືອກ
              </p>
            ) : view === "profitability" ? (
              <table className="w-full min-w-[960px] text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pr-4 pb-2 font-medium">ຊື່ງານ</th>
                    <th className="pr-4 pb-2 font-medium">ວັນທີ</th>
                    <th className="pr-4 pb-2 text-right font-medium">
                      ໃບສະເໜີລາຄາ
                    </th>
                    <th className="pr-4 pb-2 text-right font-medium">ເກັບແລ້ວ</th>
                    <th className="pr-4 pb-2 text-right font-medium">ສ່ວນຕ່າງ</th>
                    <th className="pr-4 pb-2 text-right font-medium">
                      % ເກັບໄດ້
                    </th>
                    <th className="pr-4 pb-2 text-right font-medium">ຈ່າຍແລ້ວ</th>
                    <th className="pr-4 pb-2 text-right font-medium">ກຳໄລ</th>
                    <th className="pb-2 text-right font-medium">ກຳໄລ%</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="py-3 pr-4">
                        <Link
                          to="/app/performance/events/$id"
                          params={{ id: row.id }}
                          className="inline-flex items-center gap-1 font-medium hover:underline"
                        >
                          {row.title}
                          <ExternalLinkIcon className="size-3" />
                        </Link>
                      </td>
                      <td className="whitespace-nowrap py-3 pr-4">
                        {formatPerformanceDateTime(row.eventDate)}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        {formatCurrencyBuckets(row.quoted)}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        {formatCurrencyBuckets(row.revenue)}
                      </td>
                      <td
                        className={cn(
                          "py-3 pr-4 text-right",
                          row.quotedVsActual.LAK < 0 && "text-destructive",
                        )}
                      >
                        {formatCurrencyBuckets(row.quotedVsActual)}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        {row.quotedFulfillmentPercent != null
                          ? `${row.quotedFulfillmentPercent}%`
                          : "—"}
                      </td>
                      <td className="whitespace-nowrap py-3 pr-4 text-right">
                        {formatPerformanceMoney(row.payoutPaid, "LAK")}
                      </td>
                      <td className="whitespace-nowrap py-3 pr-4 text-right">
                        {row.margin == null
                          ? "—"
                          : formatPerformanceMoney(row.margin, "LAK")}
                      </td>
                      <td className="py-3 text-right">
                        {row.marginPercent != null
                          ? `${row.marginPercent}%`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full min-w-[960px] text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pr-4 pb-2 font-medium">ຊື່ງານ</th>
                    <th className="pr-4 pb-2 font-medium">ວັນທີ</th>
                    <th className="pr-4 pb-2 font-medium">ສະຖານທີ່</th>
                    <th className="pr-4 pb-2 font-medium">ສະຖານະ</th>
                    <th className="pr-4 pb-2 text-right font-medium">
                      ນັກສະແດງ
                    </th>
                    <th className="pr-4 pb-2 text-right font-medium">
                      ໃບສະເໜີລາຄາ
                    </th>
                    <th className="pr-4 pb-2 text-right font-medium">ລາຍຮັບ</th>
                    <th className="pr-4 pb-2 text-right font-medium">ຈ່າຍແລ້ວ</th>
                    <th className="pr-4 pb-2 text-right font-medium">ກຳໄລ</th>
                    <th className="pb-2 text-right font-medium">ກຳໄລ%</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="py-3 pr-4">
                        <Link
                          to="/app/performance/events/$id"
                          params={{ id: row.id }}
                          className="inline-flex items-center gap-1 font-medium hover:underline"
                        >
                          {row.title}
                          <ExternalLinkIcon className="size-3" />
                        </Link>
                      </td>
                      <td className="whitespace-nowrap py-3 pr-4">
                        {formatPerformanceDateTime(row.eventDate)}
                      </td>
                      <td className="py-3 pr-4">{row.locationText ?? "—"}</td>
                      <td className="py-3 pr-4">
                        <Badge
                          variant={
                            STATUS_VARIANTS[
                            row.status as keyof typeof STATUS_VARIANTS
                            ] ?? "secondary"
                          }
                        >
                          {STATUS_LABELS[
                            row.status as keyof typeof STATUS_LABELS
                          ] ?? row.status}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-right">
                        {row.assignmentCount}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        {formatCurrencyBuckets(row.quoted)}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        {formatCurrencyBuckets(row.revenue)}
                      </td>
                      <td className="whitespace-nowrap py-3 pr-4 text-right">
                        {formatPerformanceMoney(row.payoutPaid, "LAK")}
                      </td>
                      <td className="whitespace-nowrap py-3 pr-4 text-right">
                        {row.margin == null
                          ? "—"
                          : formatPerformanceMoney(row.margin, "LAK")}
                      </td>
                      <td className="py-3 text-right">
                        {row.marginPercent != null
                          ? `${row.marginPercent}%`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
